import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'

export interface ConsentState {
    marketing: boolean
}

interface ConsentData {
    uuid: string
    consent: ConsentState
    version: string
    timestamp: string
}

interface ConsentContextType {
    consent: ConsentState | null
    hasDecided: boolean
    showBanner: boolean
    setShowBanner: (show: boolean) => void
    acceptAll: () => void
    rejectAll: () => void
    saveConsent: (consent: ConsentState) => void
}

const CONSENT_KEY = 'ziegler_consent'
const CONSENT_VERSION = 'v1.0'
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000 // 12 months

const ConsentContext = createContext<ConsentContextType | undefined>(undefined)

export const useConsent = (): ConsentContextType => {
    const context = useContext(ConsentContext)
    if (!context) {
        throw new Error('useConsent must be used within a ConsentProvider')
    }
    return context
}

function getStoredConsent(): ConsentData | null {
    if (typeof window === 'undefined') return null

    try {
        const raw = localStorage.getItem(CONSENT_KEY)
        if (!raw) return null

        const data: ConsentData = JSON.parse(raw)

        // Check expiry (12 months)
        const storedTime = new Date(data.timestamp).getTime()
        if (Date.now() - storedTime > CONSENT_MAX_AGE_MS) {
            localStorage.removeItem(CONSENT_KEY)
            return null
        }

        // Check version
        if (data.version !== CONSENT_VERSION) {
            localStorage.removeItem(CONSENT_KEY)
            return null
        }

        return data
    } catch {
        localStorage.removeItem(CONSENT_KEY)
        return null
    }
}

function logConsent(data: ConsentData): void {
    // Fire-and-forget consent log to Netlify Function
    fetch('/.netlify/functions/consent-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            uuid: data.uuid,
            consent: data.consent,
            version: data.version,
            timestamp: data.timestamp,
        }),
    }).catch(() => {
        // Silently fail — consent logging is best-effort
    })
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [consent, setConsent] = useState<ConsentState | null>(null)
    const [hasDecided, setHasDecided] = useState(false)
    const [showBanner, setShowBanner] = useState(false)

    useEffect(() => {
        const stored = getStoredConsent()
        if (stored) {
            setConsent(stored.consent)
            setHasDecided(true)
        } else {
            setShowBanner(true)
        }
    }, [])

    const saveConsent = useCallback((newConsent: ConsentState) => {
        const stored = getStoredConsent()
        const uuid = stored?.uuid || uuidv4()

        const data: ConsentData = {
            uuid,
            consent: newConsent,
            version: CONSENT_VERSION,
            timestamp: new Date().toISOString(),
        }

        localStorage.setItem(CONSENT_KEY, JSON.stringify(data))
        setConsent(newConsent)
        setHasDecided(true)
        setShowBanner(false)
        logConsent(data)
    }, [])

    const acceptAll = useCallback(() => {
        saveConsent({ marketing: true })
    }, [saveConsent])

    const rejectAll = useCallback(() => {
        saveConsent({ marketing: false })
    }, [saveConsent])

    return (
        <ConsentContext.Provider
            value={{
                consent,
                hasDecided,
                showBanner,
                setShowBanner,
                acceptAll,
                rejectAll,
                saveConsent,
            }}
        >
            {children}
        </ConsentContext.Provider>
    )
}
